import type { ClockOffset, NowPlaying } from "@touchscreen/api-client";

export interface TrackProgress {
  elapsedSeconds: number;
  remainingSeconds: number;
  /** 0–1, clamped. */
  ratio: number;
  /** Epoch ms this track is predicted to end, per the server clock. */
  predictedEndMs: number;
}

/**
 * Interpolates progress locally from `started_at` + `duration_seconds`, against the server's
 * clock offset — never against the device clock directly, and never by polling. See
 * touchscreen-api.md §Client rules.
 */
export function computeTrackProgress(track: NowPlaying, clock: ClockOffset): TrackProgress {
  const startedAtMs = Date.parse(track.started_at);
  const durationMs = track.duration_seconds * 1000;
  const predictedEndMs = startedAtMs + durationMs;

  const elapsedMs = clamp(clock.now() - startedAtMs, 0, durationMs);
  const elapsedSeconds = elapsedMs / 1000;
  const remainingSeconds = Math.max(0, track.duration_seconds - elapsedSeconds);
  const ratio = durationMs > 0 ? elapsedMs / durationMs : 0;

  return { elapsedSeconds, remainingSeconds, ratio, predictedEndMs };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
